import { useMemo, type ReactElement } from 'react';
import { creerClient } from '../../api/client.js';
import { COULEURS } from '../../app/styles.js';
import { ListeParDossier, useLargeurFenetre, type CelluleListe } from '../dossier/liste-par-dossier.js';
import { euros } from './modele.js';
import { aTraiterFacturation, creerSourceListeFacturation, type LigneFacturation, type SourceListeFacturation } from './source-liste.js';

/**
 * D08 sans dossier ouvert : la facturation de tous les dossiers (équipe MAAT), comme la démo. Facturé, encaissé et reste dû viennent
 * de `facturation.dossier` de chaque dossier ; un dossier sans règle ou illisible est dit tel quel, jamais présenté à zéro.
 */
const MESSAGES = {
  intro: 'Choisissez un dossier pour cocher ses règles, envoyer les liens de paiement et suivre les règlements.',
  chargement: 'Chargement de la facturation des dossiers en cours…',
  erreur: 'La facturation des dossiers n’a pas pu être chargée. Réessayez dans un instant.',
  vide: 'Aucun dossier pour le moment.',
  reglesAbsentes: 'Règles à définir',
  indisponible: 'Non disponible',
  aJour: 'À jour',
} as const;

const pluriel = (n: number, un: string, plusieurs: string): string => `${n} ${n > 1 ? plusieurs : un}`;
const puce = {
  display: 'inline-block',
  padding: '2px 8px',
  borderRadius: 8,
  fontSize: 14,
  background: COULEURS.ambreFond,
  color: COULEURS.ambre,
} as const;

function montant(l: LigneFacturation, valeur: LigneFacturation['reste']): string {
  if (l.etat === 'ok' && valeur) return euros(valeur);
  return '—';
}

function cellules(l: LigneFacturation): CelluleListe[] {
  const etat: ReactElement =
    l.etat === 'regles_absentes' ? (
      <span style={puce}>{MESSAGES.reglesAbsentes}</span>
    ) : l.etat === 'indisponible' ? (
      <span style={puce}>{MESSAGES.indisponible}</span>
    ) : l.nbImpayes > 0 || l.nbBloquantSignature > 0 ? (
      <span style={puce}>
        {l.nbImpayes > 0
          ? pluriel(l.nbImpayes, 'reste dû', 'restes dus')
          : pluriel(l.nbBloquantSignature, 'signature bloquée', 'signatures bloquées')}
      </span>
    ) : (
      <span>{MESSAGES.aJour}</span>
    );
  return [
    { titre: 'Facturé', contenu: montant(l, l.facture), nombre: true },
    { titre: 'Encaissé', contenu: montant(l, l.encaisse), nombre: true },
    { titre: 'Reste dû', contenu: montant(l, l.reste), nombre: true },
    { titre: 'À traiter', contenu: etat },
  ];
}

export function ListeFacturation({ source, largeur }: { source: SourceListeFacturation; largeur: number }): ReactElement {
  return (
    <ListeParDossier<LigneFacturation>
      nom="facturation"
      titre="Facturation"
      intro={MESSAGES.intro}
      chargement={MESSAGES.chargement}
      erreur={MESSAGES.erreur}
      vide={MESSAGES.vide}
      largeur={largeur}
      charger={source.charger}
      cellules={cellules}
      aTraiter={aTraiterFacturation}
      lien={(l) => ({
        vers: `/facturation?dossier=${encodeURIComponent(l.dossierId)}`,
        libelle: 'Ouvrir',
        aria: `Ouvrir la facturation de ${l.titre}`,
      })}
    />
  );
}

/** Branche la liste sur les routes réelles (même origine, cookie de session). */
export function ListeFacturationReelle({ fetchApi }: { fetchApi?: typeof fetch } = {}): ReactElement {
  const largeur = useLargeurFenetre();
  const source = useMemo(
    () => creerSourceListeFacturation(creerClient({ fetch: fetchApi ?? ((...args) => globalThis.fetch(...args)) })),
    [],
  );
  return <ListeFacturation source={source} largeur={largeur} />;
}
